import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { ChipSelector } from '@/components/common/Chip'
import { BaccaratTable } from './components/BaccaratTable'
import { BetAreas } from './components/BetAreas'
import { useBaccaratGame } from './hooks/useBaccaratGame'
import { formatCurrency } from '@/lib/utils'

export function Baccarat() {
  const {
    playerHand,
    bankerHand,
    selectedBet,
    betAmount,
    selectedChip,
    gamePhase,
    winner,
    lastWinnings,
    isDealing,
    dealingTo,
    selectBet,
    selectChip,
    clearBet,
    deal,
    newGame,
  } = useBaccaratGame()

  const isBetting = gamePhase === 'betting'
  const isResult = gamePhase === 'result'

  return (
    <div className="flex-1 flex flex-col bg-casino-gradient">
      {/* Game table */}
      <BaccaratTable
        playerHand={playerHand}
        bankerHand={bankerHand}
        winner={winner}
        dealingTo={dealingTo}
      />

      {/* Bet areas */}
      <BetAreas
        selectedBet={selectedBet}
        betAmount={betAmount}
        disabled={!isBetting || isDealing}
        onSelectBet={selectBet}
      />

      {/* Controls */}
      <div className="p-4 bg-gradient-to-t from-casino-black-light to-transparent">
        {isBetting && (
          <div className="space-y-4">
            {/* Chip selector */}
            <ChipSelector
              selectedValue={selectedChip}
              onSelect={selectChip}
              disabled={isDealing}
            />

            {/* Action buttons */}
            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={clearBet}
                disabled={!selectedBet || isDealing}
              >
                LIMPIAR
              </Button>
              <Button
                variant="gold"
                onClick={deal}
                disabled={!selectedBet || betAmount <= 0 || isDealing}
                className="min-w-[150px]"
              >
                {isDealing ? 'REPARTIENDO...' : 'REPARTIR'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Result overlay */}
      <AnimatePresence>
        {isResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-casino-black-light border border-casino-gold/30 rounded-xl p-8 text-center max-w-md mx-4 shadow-gold-lg"
            >
              <motion.h2
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                className="font-cinzel text-3xl text-casino-gold mb-4"
              >
                {winner === 'player' && '¡JUGADOR GANA!'}
                {winner === 'banker' && '¡BANCA GANA!'}
                {winner === 'tie' && '¡EMPATE!'}
              </motion.h2>

              {lastWinnings > 0 ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <p className="text-gray-400 mb-2">Ganaste</p>
                  <p className="text-4xl font-bold text-casino-gold-light">
                    {formatCurrency(lastWinnings)}
                  </p>
                </motion.div>
              ) : lastWinnings === betAmount ? (
                <p className="text-gray-400">Se devuelve tu apuesta</p>
              ) : (
                <p className="text-gray-400">Perdiste esta mano</p>
              )}

              <Button
                variant="gold"
                onClick={newGame}
                className="mt-6"
              >
                NUEVA MANO
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
